/**
 * @module lib/darwin
 */
import { getProp, setProp, notIE } from "utils/utils";
import { local, isLocalStorageTypeSupported } from "lib/store";
import logger from "utils/logger";

/**
 * @method getSegment
 * @description Used in the context of a darwin/test feature, allows the developer to add code to
 * the feature's "Segments JavaScript" custom field for comparison against window.tgam.datalayer properties.
 * When the first argument is "device", the function is used to check whether or not we're in a mobile/desktop context.
 * @example
 *   if (!getSegment('identity.role.id', 0)) {
 *    // the user in this segment's role id is not anonymous
 *   }
 * @example:
 *  if (getSegment("device", "mobile")) {
 *    // user in this segment is on a mobile device
 *  }
 * @param {String} obj - A string  that can either be "device" or a 'stringified' version of a datalayer property
 * @param {String} value - The value to look for in obj
 * @returns {Boolean}
 */
function getSegment(obj, value) {
  if (obj === "device") {
    return ((value === "mobile" && window.matchMedia("(max-width: 1024px)").matches) ||
    (value === "desktop" && !window.matchMedia("(max-width: 1024px)").matches));
  }
  try {
    let datalayerProp = getProp(window, `tgam.datalayer.${obj}`);
    return datalayerProp !== null && datalayerProp === value;
  } catch (e) {
    if (window.console && window.console.error) {
      throw new Error(`Error accessing '${obj}' from datalayer in Darwin`);
    }
    return false;
  }
}

/**
 * @method setKeytarWall
 * @description sets a property of the global window.tgam.darwin.keytarRender object
 * @param {String} key - should match one of the Keytar templates' darwinRenderKey values so that the matching template can fetch the appropriate data from the tgam.darwin.keytar object via its darwinRendering method
 * @param {String} viewType - of the wall (modal or teaser)
 * @param {Object} [configs={}] - darwin configs for the teaser wall - template and the number of paragraphs to insert after
 */
function setKeytarWall(key, viewType, configs = {}) {
  const { template = () => {}, paragraphs = 0 } = configs;
  /* Possible cases:
    1) Display the regular paywall (`setKeytarWall` function won't be used at all. Show our regular paywall)
    2) Takeover paywall (provide "modal" parameter, provide the function),
      setKeytarWall("hardPaywall", "modal", { template: renderHardPayWall })
    3) Display the teaser paywall (provide "teaser" parameter, no function needed, just use our default paywall but in teaser mode)
      setKeytarWall("hardPaywall", "teaser")
    4) Takeover the teaser wall (provide "teaser" parameter, provide the function)
      setKeytarWall("hardPaywall", "teaser", { template: renderHardPaywall })
    5) Any wall with viewType `teaser` can be configured with the number of paragraphs to display the wall after,
      if no value provided - wall will be displayed after the second paragraph
      setKeytarWall("hardPaywall", "teaser", { template: renderHardPaywall, paragraphs: 4 });
  */
  setProp(window, `tgam.darwin.keytar[${key}]`, { viewType, template, paragraphs });
  logger.info(`[KEYTAR] setKeytarWall called with key: ${key}, viewType: ${viewType}, template: ${template} and paragraphs: ${paragraphs}`);
}

/**
 * @method getDarwinState
 * @description retrieves all Darwin test data in localStorage
 * @returns {Object} - all tests or an empty object
 */
function getDarwinState() {
  const tests = isLocalStorageTypeSupported("localStorage") && local.get("tgam.darwin_tests");

  return tests || {};
}

/**
 * @method setDarwinState
 * @description saves a test to localStorage
 * @param {String} testName - the name of the test
 * @param {String} key - the key name
 * @param {String} value - the value to be saved
 */
function setDarwinState(testName, key, value) {
  let dw = getDarwinState();
  if (dw[testName]) {
    dw[testName][key] = value;
  } else {
    dw[testName] = {};
    dw[testName][key] = value;
  }
  isLocalStorageTypeSupported("localStorage") && local.set("tgam.darwin_tests", dw);
}

/**
 * @method setDarwinVariant
 * @description saves a test variant to localStorage
 * @param {String} testId - the id of the test
 * @param {String} variant - the variant name
 */
function setDarwinVariant(testId, variant) {
  // The false param ensures that we don't serialize (wrap with quotes) the variant value string.
  isLocalStorageTypeSupported("localStorage") && local.set(`Dw__${testId}|global`, variant, false);
}

/**
 * @method setUnitState
 * @description records that a test variant was seen
 * @param {String} testId - the id of the test
 * @param {String} variant - the variant name
 */
function setUnitState(testId, variant) {
  const dw = getDarwinState();
  let hasSeenUnit = 1;
  if (dw.hasOwnProperty(testId) && dw[testId].hasSeenUnit !== undefined) {
    hasSeenUnit += dw[testId].hasSeenUnit;
  }
  if (hasSeenUnit === 1) {
    setDarwinState(testId, "variant", variant);
  }
  setDarwinState(testId, "hasSeenUnit", hasSeenUnit);
}

/**
 * @method init
 * @description Expose methods globally so as to not break existing Darwin tests that rely on "darwin-bg.html".
 */
export default function init() {
  const HMS_BEAGLE = {
    getDarwinState,
    getState: getDarwinState,
    getSegment,
    setDarwinState,
    setState: setDarwinState,
    setDarwinVariant,
    setVariant: setDarwinVariant,
    setKeytarWall,
    setUnitState,
    notIE
  };

  Object.assign(getProp(window, "tgam.darwin", {}), HMS_BEAGLE);

  // TODO: In the future, this should be removed.
  for (let prop in HMS_BEAGLE) {
    window[prop] = window.tgam.darwin[prop];
  }
}
